import { PickingInfo } from '@babylonjs/core/Collisions/pickingInfo';
import { KeyboardInfo } from '@babylonjs/core/Events/keyboardEvents';
import { PointerInfo, PointerInfoBase } from '@babylonjs/core/Events/pointerEvents';
import { BoundingBoxInfo } from '@kb3d/view/modules';
import { AnnotationNode } from './annotations';
import { eConnectionMode, eDragMode, eMateType } from './enums';
import { Connector } from './features/connector';
import { Kb3dManager } from './kb3d-manager';
import { Kb3dObject } from './kb3d-object';
import { MateNode } from './mates/mate-node';
import { isPickable } from './meshes/mesh-utilities';
import { SpaceNode } from './meshes/space-node';
import { KbVector } from './primitives';

export type PointerInfoMinimal = PointerInfoBase & Pick<PointerInfo, 'pickInfo'>;

/** A 2 layer map that stores values by a key 1st, then a 2nd level of a string tag, then an array of values */
export class TagArrayMap<TKey, TValue> {
    private _db = new Map<TKey, ArrayMap<string, TValue>>();

    public get(key: TKey, tag: string) {
        const v = this._db.get(key)?.get(tag);
        return v || [];
    }

    public deleteByKeyValue(key: TKey, value: TValue) {
        const e = this._db.get(key);
        if (e) {
            for (const values of e.values()) {
                values.removeWhere(v => v == value);
            }
            this.deleteEntryIfEmpty(key);
        }
    }

    public getByKey(key: TKey): TValue[] {
        if (this._db.has(key)) {
            return (this._db.get(key) as ArrayMap<string, TValue>).all();
        }
        return [];
    }

    public set(key: TKey, tag: string, value: TValue) {
        if (!this._db.has(key)) this._db.set(key, new ArrayMap<string, TValue>());
        this._db.get(key)?.add(tag, value);
    }

    public deleteByTag(...tags: string[]) {
        const result: Array<{ node: TKey; handlers: Record<string, TValue[]> }> = [];
        for (const [key, value] of this._db.entries()) {
            const nodeResult: Record<string, TValue[]> = {};
            result.push({ node: key, handlers: nodeResult });

            for (const tag of tags) {
                const handler = value.get(tag);
                if (handler) {
                    nodeResult[tag] = handler;
                }

                value.delete(tag);
            }
            this.deleteEntryIfEmpty(key);
        }
        return result;
    }

    public delete(key: TKey, tag: string) {
        const e = this._db.get(key);
        if (e) {
            const result = e.get(tag);
            e.delete(tag);
            this.deleteEntryIfEmpty(key);
            return result;
        }
        return [];
    }

    public deleteByKey(key: TKey) {
        const handlers = this._db.get(key);
        const result: Record<string, TValue[]> = {};
        if (handlers) {
            handlers.forEach((value, key) => {
                result[key] = value;
            });
        }

        this._db.delete(key);

        return result;
    }

    public clear() {
        this._db.clear();
    }

    public has(key: TKey, tag: string) {
        return this.get(key, tag).length > 0;
    }

    protected deleteEntryIfEmpty(key: TKey) {
        const e = this._db.get(key);
        if (e && !e.size) this._db.delete(key);
    }
}

export class ArrayMap<TKey, TValue> extends Map<TKey, TValue[]> {
    public get(key: TKey) {
        return super.get(key) || [];
    }

    public add(key: TKey, ...values: TValue[]) {
        if (!this.has(key)) {
            super.set(key, [...values]);
        } else {
            this.get(key).push(...values);
        }
    }

    public removeValue(key: TKey, ...values: TValue[]) {
        const arr = super.get(key);
        if (arr) {
            arr.removeWhere(i => values.some(v => v == i));
        }
    }

    public all() {
        return [...this.values()].flat();
    }
}

export class SetMap<TKey, TValue> extends Map<TKey, Set<TValue>> {
    public get(key: TKey) {
        const set = super.get(key);
        if (!set) super.set(key, new Set<TValue>());
        return super.get(key)!;
    }

    public add(key: TKey, ...values: TValue[]) {
        const s = this.get(key);
        for (const v of values) s.add(v);
    }

    public removeValue(key: TKey, ...values: TValue[]) {
        const s = super.get(key);
        if (s) {
            for (const v of values) s.delete(v);
        }
    }

    public all() {
        return [...this.values()].flat();
    }
}

export class HandlerMap<THandler> extends TagArrayMap<SpaceNode | AnnotationNode, THandler> {
    constructor(private manager: Kb3dManager) {
        super();
    }

    public add(idNameOrNode: string | SpaceNode | AnnotationNode, tag: string, handler: THandler) {
        let nodes = new Array<Kb3dObject>();
        if (typeof idNameOrNode == 'string') {
            nodes = this.manager.getNodesByIdOrName(idNameOrNode, true);
        } else {
            nodes.push(idNameOrNode);
        }
        for (const n of nodes) {
            if (isPickable(n)) {
                this.set(n, tag, handler);
            }
        }
    }
}
